/* eslint-disable consistent-return */
/* eslint-disable max-len */
/* eslint-disable react/no-array-index-key */
/* eslint-disable jsx-a11y/role-has-required-aria-props */
import classnamesBind from 'classnames/bind';
import Icon from '@concur/nui-widgets/lib/Icon/Icon';
import React from 'react';
import PropTypes from 'prop-types';
import shortId from '@concur/nui-widgets/lib/utils/shortId';
import Menu from './_Menu';
import lowerCase from '../utils/lowerCase';

const renderListItemChild = (
    classNameMap,
    cssBlock,
    children,
    formatter,
    hasPopover,
    testId,
    submenuHeadingId,
    headingId,
    labelId,
    isOverflow,
    isSubItem,
    name,
    anchorProps,
    dropdownProps,
    subItems,
) => {
    const classnames = classnamesBind.bind(classNameMap);

    if (children) {
        return (
            <a
                {...anchorProps}
                {...dropdownProps}
                tabIndex="0" /* eslint-disable-line jsx-a11y/no-noninteractive-tabindex */
                aria-describedby={headingId || labelId}
            >
                {children}
            </a>
        );
    }

    const iconName = isSubItem ? 'arrow-1-e' : 'arrow-1-s';
    const iconClasses = classnames(
        `${cssBlock}__icon`,
        {
            [`${cssBlock}__icon--squash`]: !isSubItem,
        },
    );

    if (subItems || isOverflow) {
        return (
            <span
                {...dropdownProps}
                data-test={testId ? `menu__button-${lowerCase(testId)}` : null}
                role="button"
                id={submenuHeadingId}
                tabIndex={hasPopover ? '0' : '-1'}
            >
                {/* TODO: ideally, make this unaware of branding -- Icon is currently specific to Concur icons */}
                {isOverflow ? (<Icon ariaLabel={formatter?.formattedMessage({ id: 'CoreUI.moreLinksLabel' })} className={classnames(`${cssBlock}--overflow`)} iconName="menu" size="md" />) : name}
                {!isOverflow && subItems && (
                    <>
                        {' '}
                        <Icon ariaHidden className={iconClasses} iconName={iconName} />
                    </>
                )}
            </span>
        );
    }

    return (
        <a
            {...anchorProps}
            role="menuitem"
            tabIndex="0"
            aria-describedby={headingId || labelId}
        >
            {name}
        </a>
    );
};

const MenuItem = React.forwardRef((props, ref) => {
    const {
        anchorClassName,
        children,
        className,
        classNameMap,
        cssBlock,
        depth,
        expanded,
        formatter,
        hasPopover,
        id,
        headingId,
        isActive,
        isDivider,
        isFioriProductMenu,
        isHeading,
        isHidden,
        isOverflow,
        isPipe,
        isSubItem,
        isSubItemMenuAlignRight,
        isSubItemMenuUtility,
        labelId,
        name,
        parentMenuCssBlock,
        subItems,
        target,
        url,
        ...otherProps
    } = props;

    const classnames = classnamesBind.bind(classNameMap);

    if (isDivider) {
        return <hr aria-hidden="true" className={classnames(`${cssBlock}__divider`)} />;
    }

    if (isPipe) {
        return <span aria-hidden="true" className={classnames(`${cssBlock}__pipe`)} />;
    }

    if (isHeading) {
        return (
            <li
                {...otherProps}
                className={classnames(className, `${cssBlock}__heading`)}
                role="heading"
                id={id}
            >
                {children}
            </li>
        );
    }

    const classes = classnames(`${cssBlock}__listitem`, className, {
        [`${cssBlock}__listitem--hidden`]: isHidden,
    });

    const anchorClasses = classnames(`${cssBlock}__anchor`, anchorClassName, {
        [`${cssBlock}__anchor--active`]: isActive,
        [`${cssBlock}__anchor--submenu`]: !!subItems,
        [`${cssBlock}__anchor--expanded`]: expanded,
    });

    // if any menu items are trying to use window.open, the target attribute needs to be removed
    const isTargetAttributeSupported = () => !url?.match(/window\.open/);

    const anchorProps = {
        'aria-label': name,
        className: anchorClasses,
        'data-test': id ? `menu__anchor-${lowerCase(id)}` : null,
        href: url,
        target: isTargetAttributeSupported() ? target : null,
        tabIndex: '0',
    };

    const dropdownProps = subItems && {
        className: anchorClasses,
        'data-test': id ? `menu__dropdown-${lowerCase(id)}` : null,
        href: null,
        role: 'button',
        target: null,
        onClick: (e) => e.preventDefault(),
        'aria-haspopup': true,
        'aria-expanded': expanded,
    };

    const subItemMenuClasses = classnames(`${cssBlock}__submenu`, {
        [`${cssBlock}__submenu--expanded`]: expanded,
        [`${cssBlock}__submenu--right`]: isSubItemMenuAlignRight || isOverflow,
        [`${cssBlock}__submenu--utility`]: isSubItemMenuUtility,
        [`${cssBlock}__submenu--level${depth}`]: depth,
    });

    const submenuHeadingId = shortId.generate();

    return (
        // jsx-a11y/role-supports-aria-props: disabled due to role="none"
        // role="none" removes the implied listitem role of the li element
        // parent ul is serving as a menu so the li elements are not in their required list context
        // See: https://www.w3.org/TR/wai-aria-practices/examples/menubar/menubar-1/menubar-1.html#
        // jsx-a11y/mouse-events-have-key-events: disabled due to onFocus and onBlur being covered by onKeyDown function
        // eslint-disable-next-line jsx-a11y/role-supports-aria-props, jsx-a11y/mouse-events-have-key-events
        <li
            // setting this to "false" will cause the role to resolve to listitem
            // and is inconsistent across browsers https://www.w3.org/TR/wai-aria-1.1/#aria-hidden
            aria-hidden={isHidden ? true : undefined}
            {...otherProps}
            className={classes}
            data-test={id ? `menu__listitem-${lowerCase(id)}` : null}
            ref={ref}
            aria-describedby={labelId}
        >
            {renderListItemChild(
                classNameMap,
                cssBlock,
                children,
                formatter,
                hasPopover,
                id,
                submenuHeadingId,
                headingId,
                labelId,
                isOverflow,
                isSubItem,
                name,
                anchorProps,
                dropdownProps,
                subItems,
            )}
            {subItems && !isOverflow && (
                <Menu
                    className={subItemMenuClasses}
                    id={id}
                    isSubItem
                    parentMenuCssBlock={parentMenuCssBlock}
                    isFioriProductMenu={isFioriProductMenu}
                >
                    {subItems.map((item, index) => (
                        <MenuItem
                            {...item}
                            parentMenuCssBlock={parentMenuCssBlock}
                            classNameMap={classNameMap}
                            cssBlock={cssBlock}
                            depth={depth + 1}
                            key={`subItem${index}`}
                            hasPopover={hasPopover}
                            isSubItem
                            isSubItemMenuUtility={isSubItemMenuAlignRight}
                            labelId={submenuHeadingId}
                        />
                    ))}
                </Menu>
            )}
            {subItems && isOverflow && (
                <Menu
                    ariaLabel={name}
                    className={subItemMenuClasses}
                    parentMenuCssBlock={parentMenuCssBlock}
                    isFioriProductMenu={isFioriProductMenu}
                >
                    {React.Children.map(subItems, (child) => React.cloneElement(child, { isSubItem: true, hasPopover }))}
                </Menu>
            )}
        </li>
    );
});

MenuItem.displayName = 'MenuItem';

MenuItem.apiPropTypes = {
    name: PropTypes.string,
    url: PropTypes.string,
    id: PropTypes.string,
    isActive: PropTypes.bool,
    isDivider: PropTypes.bool,
    subItems: PropTypes.arrayOf(PropTypes.object),
    target: PropTypes.string,
};

MenuItem.defaultProps = {
    depth: 1,
};

MenuItem.propTypes = {
    ...MenuItem.apiPropTypes,
    anchorClassName: PropTypes.string,
    children: PropTypes.node,
    className: PropTypes.string,
    cssBlock: PropTypes.string,
    depth: PropTypes.number,
    hasPopover: PropTypes.bool,
    headingId: PropTypes.string,
    isHeading: PropTypes.bool,
    isHidden: PropTypes.bool,
    isOverflow: PropTypes.bool,
    isSubItem: PropTypes.bool,
    isPipe: PropTypes.bool,
    isSubItemMenuAlignRight: PropTypes.bool,
    labelId: PropTypes.string,
};

export default MenuItem;
